import type { DashboardSummary } from "@player115/contracts";
import { Activity, Laptop, RefreshCw, ShieldAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../api";
import { PageState, Status } from "../components/Status";

const actionLabels: Record<string, string> = {
  "feature.updated": "更新模块策略",
  "release.updated": "更新版本策略",
  "release.created": "创建版本策略",
};

function relativeTime(value: string) {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 60_000));
  if (minutes < 60) return `${minutes || 1} 分钟前`;
  if (minutes < 1_440) return `${Math.round(minutes / 60)} 小时前`;
  return `${Math.round(minutes / 1_440)} 天前`;
}

export function DashboardPage() {
  const [data, setData] = useState<DashboardSummary | null>(null);
  const [error, setError] = useState("");
  const load = async () => {
    setError("");
    try { setData(await api.dashboard()); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "概览读取失败"); }
  };
  useEffect(() => { void load(); }, []);
  if (error) return <PageState title="概览暂时不可用" detail={error} action={<button className="secondary-button" type="button" onClick={() => void load()}><RefreshCw size={16} />重试</button>} />;
  if (!data) return <div className="skeleton-page" aria-label="正在加载"><i /><i /><i /></div>;
  const maxVersionCount = Math.max(...data.versions.map((item) => item.count), 1);
  return <section className="page">
    <header className="page-head"><div><h1>数据概览</h1><p>客户端活跃、版本覆盖与控制策略</p></div><Status tone="success">控制服务正常</Status></header>
    <div className="metric-strip">
      <article><span><Laptop size={18} />累计安装</span><strong>{data.clients.total.toLocaleString("zh-CN")}</strong><small>匿名安装实例</small></article>
      <article><span><Activity size={18} />24 小时活跃</span><strong>{data.clients.active24h.toLocaleString("zh-CN")}</strong><small>7 日活跃 {data.clients.active7d.toLocaleString("zh-CN")}</small></article>
      <article><span><RefreshCw size={18} />生效版本策略</span><strong>{data.releases.activePolicies}</strong><small>{data.releases.forcedPolicies} 项强制升级</small></article>
      <article><span><ShieldAlert size={18} />权益模块</span><strong>{data.features.entitlement}</strong><small>{data.features.disabled} 项全局停用</small></article>
    </div>
    <div className="dashboard-columns">
      <section className="data-section version-distribution">
        <header><div><h2>版本分布</h2><p>最近上报的客户端版本</p></div><span>{data.versions.reduce((sum, item) => sum + item.count, 0).toLocaleString("zh-CN")} 台</span></header>
        {data.versions.length ? <div className="version-list">{data.versions.map((item) => <div className="version-row" key={item.version}><strong>v{item.version}</strong><div><i style={{ width: `${item.count / maxVersionCount * 100}%` }} /></div><span>{item.count.toLocaleString("zh-CN")}</span></div>)}</div> : <PageState title="暂无版本数据" detail="客户端完成首次版本检查后会显示在这里。" />}
      </section>
      <section className="data-section audit-section">
        <header><div><h2>最近操作</h2><p>版本和模块策略变更</p></div></header>
        {data.recentAudit.length ? <div className="audit-list">{data.recentAudit.map((item) => <div className="audit-row" key={item.id}><i aria-hidden="true" /><div><strong>{actionLabels[item.action] ?? item.action}</strong><small>{item.targetId || item.targetType}</small></div><span><b>{item.actorName}</b>{relativeTime(item.createdAt)}</span></div>)}</div> : <PageState title="暂无审计记录" detail="首次保存策略后会生成记录。" />}
      </section>
    </div>
  </section>;
}
